import React from 'react';
import { observer } from 'mobx-react';
import { routerStore } from 'core/routing';

import { analytics } from 'core/analytics';
import { frameStore } from 'apps/components/Frame/appManager';
import { PanelTypes } from 'constants/global';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { getFormatObject } from 'libs/rendering/utils';
import { clientStore } from 'apps/client/app';

// eslint-disable-next-line prettier/prettier
import type { Space as Workspace } from 'generated/typescript';
import { themes } from 'styles/themes/wikifactoryNew';
import Tooltip from 'components/Tooltip';
import { MakeButtonStyled } from './headerFileFeatures.styled';

interface MakeButtonContainerProps {
  dataTestIdPrefix?: string;
}

export const MakeButtonContainer: React.FC<MakeButtonContainerProps> = observer(
  ({ dataTestIdPrefix = '' }) => {
    const { file, sharedFileManager } = frameStore;
    const routeName = routerStore.route.name;

    const viewerControl = useViewerControl();
    const routeParams = routerStore.route.params;
    const { panel } = routeParams;

    if (!file) return null;
    // In these routes this is just an Info button to show toggle the UI, no need to check
    // properties of whether the file can be Made.
    if (!frameStore.isRouteWithInfoButton) {
      const { formatObject, extension } = getFormatObject(file);

      if (formatObject.viewer === 'GltfViewer') {
        if (!viewerControl.assemblyReady) return null;
      } else if (extension !== 'pdf') return null;
    }

    // If we are in Make button mode, hide it in some cases
    if (!frameStore.isRouteWithInfoButton) {
      // The follow-up code should override this one anyway
      if (
        sharedFileManager?.sharedFile &&
        // Hide button for limited-collaborators (canUpdate shows permission access)
        !sharedFileManager.sharedFile.canUpdate
      ) {
        return null;
      }

      if (!file.space) {
        return null;
      }

      const fileParentSpaceId = file.space.parent.id;
      const isFileParentSpaceInOurWorkspaces = clientStore.workspaces.some(
        (workspace: Workspace) => workspace.id === fileParentSpaceId,
      );
      if (file.private && !isFileParentSpaceInOurWorkspaces) return null;
    }

    const isActive = frameStore.canShowRightPanel;

    let text = 'Make';
    if (frameStore.isRouteWithInfoButton) text = 'Info';

    const handleClick = () => {
      let panelParamValue = '';
      if (frameStore.isRouteWithInfoButton) panelParamValue = 'info';
      else if (frameStore.isRouteWithMakeButton) panelParamValue = 'rfq';

      const newParams = { ...routeParams };

      if (newParams.panel === panelParamValue) delete newParams.panel;
      else newParams.panel = panelParamValue;
      routerStore.navigate(routeName, newParams, { replace: true });
    };

    const disabled =
      (!clientStore.isBusinessPlanOfCurrentSpace ||
        clientStore.isInProfileSpace) &&
      !clientStore.hasRole('INTERMEDIATOR') &&
      CI === 'false';

    const disabledStyle = disabled
      ? {
          opacity: '0.5',
          userSelect: 'none',
          cursor: 'not-allowed',
        }
      : {};

    return (
      <>
        <div className='hover:opacity-70'>
          <MakeButtonStyled
            data-tip='Upgrade to Business'
            data-for='make-part-tip'
            onClick={() => {
              if (!disabled) {
                analytics.sendEvent(
                  'make-part-click-cad-rooms',
                  {},
                  { ga: true },
                );
                handleClick();
              }
            }}
            icon='factory'
            iconPosition='right'
            style={{
              fontWeight: 600,
              background: PanelTypes.includes(panel)
                ? themes.color.wfGrey200
                : themes.color.wfWhite,
              border: 'none',
              borderBottom: `1px solid ${
                isActive ? 'transparent' : themes.color.wfGrey100
              }`,
              ...disabledStyle,
            }}
            text={text}
            data-testid={`${dataTestIdPrefix}make-button`}
            active={isActive}
            appearance='plain'
            removeBold
          />
        </div>
        {disabled ? (
          <Tooltip id='make-part-tip' delayShow={200} place='bottom' />
        ) : null}
      </>
    );
  },
);
